import YouTubeIcon from '@mui/icons-material/YouTube';
import { Box, Tooltip } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import { youtubeNativeStationTooltipMessage } from './station-messages';
import { MinimumStation, Station } from '@app/core/types/stations';

type YoutubeNativeStationIconSize = 'small' | 'medium';

const ICON_FONT_SIZE: Record<YoutubeNativeStationIconSize, string> = {
  small: '0.875rem',
  medium: '1.1rem',
};

interface YoutubeNativeStationIconProps {
  station?: Station | MinimumStation | null;
  size?: YoutubeNativeStationIconSize;
  sx?: SxProps<Theme>;
  iconSx?: SxProps<Theme>;
}

function isYoutubeNativeStation(station: Station | MinimumStation): station is Station {
  return 'youtubeNative' in station && station.youtubeNative;
}

/**
 * Renders a YouTube-native indicator icon when the station has the youtubeNative flag enabled.
 */
export function YoutubeNativeStationIcon({
  station,
  size = 'medium',
  sx,
  iconSx,
}: YoutubeNativeStationIconProps) {
  const { $t } = useIntl();

  if (!station || !isYoutubeNativeStation(station)) {
    return null;
  }

  return (
    <Tooltip title={$t(youtubeNativeStationTooltipMessage)}>
      <Box component="span" display="inline-flex" alignItems="center">
        <Box
          sx={
            [
              {
                display: 'inline-flex',
                alignItems: 'center',
                color: 'text.secondary',
                flexShrink: 0,
              },
              sx,
            ] as SxProps<Theme>
          }
        >
          <YouTubeIcon
            sx={
              [
                {
                  fontSize: ICON_FONT_SIZE[size],
                },
                iconSx,
              ] as SxProps<Theme>
            }
          />
        </Box>
      </Box>
    </Tooltip>
  );
}
